import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { ChangeEvent, FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useUserProfile } from 'contexts/UserProfileContext';
import { RepairOrderStatusE, WipStatusE } from 'declarations/enums/wip';
import type { RoofTopI } from 'declarations/interfaces/roofTop';
import type { DealerCardResponseI, OemFamilyClaimStatusesI } from 'declarations/interfaces/wip';
import { useFetchWipDealerSummary, useFetchWipOemFamilyClaimStatuses } from 'hooks/api/useWip';
import type { WipAppliedFiltersI } from 'reduxStore/features/wip/wipSlice';
import { selectWipAppliedFilters, setWipAppliedFilters } from 'reduxStore/features/wip/wipSlice';
import { useAppDispatch, useAppSelector } from 'reduxStore/hooks';

type WIPFiltersDropdownPropsT = {
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  onClose: () => void;
};

type SectionKeyT =
  | 'dealers'
  | 'oems'
  | 'wipStatus'
  | 'claimStatus'
  | 'roStatus'
  | 'backlog'
  | 'dateRange';

type SectionT = {
  key: SectionKeyT;
  label: string;
  visible: boolean;
};

const LEFT_PANEL_WIDTH = 225;

const SECTIONS: SectionT[] = [
  { key: 'dealers', label: 'Dealers', visible: true },
  { key: 'oems', label: 'OEMs', visible: true },
  { key: 'wipStatus', label: 'WIP Status', visible: true },
  { key: 'claimStatus', label: 'Claim Status', visible: true },
  { key: 'roStatus', label: 'RO Status', visible: true },
  { key: 'backlog', label: 'Backlog', visible: true },
  { key: 'dateRange', label: 'Date range', visible: true },
];

const WIP_STATUS_OPTIONS: { label: string; value: WipStatusE }[] = [
  { label: 'Pending', value: WipStatusE.Pending },
  { label: 'Working', value: WipStatusE.Working },
  { label: 'Completed', value: WipStatusE.Completed },
];

const RO_STATUS_OPTIONS: { label: string; value: RepairOrderStatusE }[] = [
  { label: 'Open', value: RepairOrderStatusE.OpenProcessing },
  { label: 'Closed', value: RepairOrderStatusE.Completed },
];

export const WIPFiltersDropdown: FC<WIPFiltersDropdownPropsT> = ({
  anchorEl,
  isOpen,
  onClose,
}): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const applied = useAppSelector(selectWipAppliedFilters);
  const { userProfile } = useUserProfile();
  const { data: dealerSummary } = useFetchWipDealerSummary();
  const { data: oemClaimStatuses } = useFetchWipOemFamilyClaimStatuses();

  const [activeSection, setActiveSection] = useState<SectionKeyT | null>(null);
  const [dealerSearch, setDealerSearch] = useState<string>('');

  useEffect((): void => {
    if (!isOpen) {
      setActiveSection(null);
      setDealerSearch('');
    }
  }, [isOpen]);

  const mappedRooftops: RoofTopI[] = useMemo(
    (): RoofTopI[] => userProfile?.mappedRoofTops ?? [],
    [userProfile?.mappedRoofTops]
  );

  const dealers: RoofTopI[] = useMemo((): RoofTopI[] => {
    const summaryIds = new Set(
      (dealerSummary?.dealers ?? []).map((d: DealerCardResponseI['dealers'][number]): string =>
        String(d.roofTopId)
      )
    );
    const filtered = mappedRooftops.filter((r): boolean => summaryIds.has(r.id));
    return filtered.length > 0 ? filtered : mappedRooftops;
  }, [dealerSummary?.dealers, mappedRooftops]);

  const filteredDealers: RoofTopI[] = useMemo((): RoofTopI[] => {
    if (!dealerSearch.trim()) return dealers;
    const term = dealerSearch.toLowerCase();
    return dealers.filter((r): boolean => r.name.toLowerCase().includes(term));
  }, [dealers, dealerSearch]);

  const oemOptions: { label: string; value: string }[] = useMemo(() => {
    const map = new Map<string, string>();
    (oemClaimStatuses ?? []).forEach((o: OemFamilyClaimStatusesI): void => {
      map.set(o.oemFamilyId, o.oemFamilyName);
    });
    return Array.from(map.entries())
      .map(([id, name]): { label: string; value: string } => ({ label: name, value: id }))
      .sort((a, b): number => a.label.localeCompare(b.label));
  }, [oemClaimStatuses]);

  const claimStatusOptions: string[] = useMemo((): string[] => {
    const set = new Set<string>();
    (oemClaimStatuses ?? []).forEach((o: OemFamilyClaimStatusesI): void => {
      o.claimStatuses.forEach((c): void => {
        set.add(c);
      });
    });
    return Array.from(set).sort((a, b): number => a.localeCompare(b));
  }, [oemClaimStatuses]);

  const updateFilters = useCallback(
    (patch: Partial<WipAppliedFiltersI>): void => {
      dispatch(setWipAppliedFilters({ ...applied, ...patch }));
    },
    [applied, dispatch]
  );

  const toggleInList = <T extends string>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((v): boolean => v !== value) : [...list, value];

  const handleToggleDealer = useCallback(
    (id: string): void => {
      updateFilters({ roofTopIds: toggleInList(applied.roofTopIds, id) });
    },
    [applied.roofTopIds, updateFilters]
  );

  const handleAllDealers = useCallback((): void => {
    updateFilters({ roofTopIds: [] });
  }, [updateFilters]);

  const handleToggleOem = useCallback(
    (id: string): void => {
      updateFilters({ oemFamilyIds: toggleInList(applied.oemFamilyIds, id) });
    },
    [applied.oemFamilyIds, updateFilters]
  );

  const handleToggleWipStatus = useCallback(
    (value: WipStatusE): void => {
      updateFilters({ wipStatuses: toggleInList(applied.wipStatuses, value) });
    },
    [applied.wipStatuses, updateFilters]
  );

  const handleToggleClaimStatus = useCallback(
    (value: string): void => {
      updateFilters({ claimStatuses: toggleInList(applied.claimStatuses, value) });
    },
    [applied.claimStatuses, updateFilters]
  );

  const handleToggleRoStatus = useCallback(
    (value: RepairOrderStatusE): void => {
      updateFilters({ repairOrderStatuses: toggleInList(applied.repairOrderStatuses, value) });
    },
    [applied.repairOrderStatuses, updateFilters]
  );

  const handleBacklogChange = useCallback(
    (value: boolean): void => {
      updateFilters({ backlog: applied.backlog === value ? null : value });
    },
    [applied.backlog, updateFilters]
  );

  const handleDateChange = useCallback(
    (key: 'createdAtFrom' | 'createdAtTo') =>
      (event: ChangeEvent<HTMLInputElement>): void => {
        updateFilters({ [key]: event.target.value || undefined });
      },
    [updateFilters]
  );

  const handleBack = (): void => {
    setActiveSection(null);
    setDealerSearch('');
  };

  const handleDealerSearchChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setDealerSearch(e.target.value);
  };

  const clearDealerSearch = (): void => {
    setDealerSearch('');
  };

  if (!isOpen || !anchorEl) return null;

  const renderDealersContent = (): JSX.Element => (
    <Box>
      <TextField
        size="small"
        fullWidth
        placeholder="Search dealers..."
        value={dealerSearch}
        onChange={handleDealerSearchChange}
        sx={{ px: 1, pt: 1, pb: 0.5 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: dealerSearch ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={clearDealerSearch}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
      <Divider sx={{ mt: 0.5 }} />
      <Box>
        <MenuItem onClick={handleAllDealers}>
          <ListItemIcon>
            <Checkbox checked={applied.roofTopIds.length === 0} size="small" />
          </ListItemIcon>
          <ListItemText primary="All" />
        </MenuItem>
        {filteredDealers.map(
          (dealer): JSX.Element => (
            <MenuItem key={dealer.id} onClick={(): void => handleToggleDealer(dealer.id)}>
              <ListItemIcon>
                <Checkbox checked={applied.roofTopIds.includes(dealer.id)} size="small" />
              </ListItemIcon>
              <ListItemText primary={dealer.name} />
            </MenuItem>
          )
        )}
      </Box>
    </Box>
  );

  const renderOemContent = (): JSX.Element => {
    if (oemOptions.length === 0) {
      return (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>No OEMs available.</Typography>
        </Box>
      );
    }
    return (
      <Box>
        {oemOptions.map(
          (opt): JSX.Element => (
            <MenuItem key={opt.value} onClick={(): void => handleToggleOem(opt.value)}>
              <ListItemIcon>
                <Checkbox checked={applied.oemFamilyIds.includes(opt.value)} size="small" />
              </ListItemIcon>
              <ListItemText primary={opt.label} />
            </MenuItem>
          )
        )}
      </Box>
    );
  };

  const renderWipStatusContent = (): JSX.Element => (
    <Box>
      {WIP_STATUS_OPTIONS.map(
        (opt): JSX.Element => (
          <MenuItem key={opt.value} onClick={(): void => handleToggleWipStatus(opt.value)}>
            <ListItemIcon>
              <Checkbox checked={applied.wipStatuses.includes(opt.value)} size="small" />
            </ListItemIcon>
            <ListItemText primary={opt.label} />
          </MenuItem>
        )
      )}
    </Box>
  );

  const renderClaimStatusContent = (): JSX.Element => {
    if (claimStatusOptions.length === 0) {
      return (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            No claim statuses available.
          </Typography>
        </Box>
      );
    }
    return (
      <Box>
        {claimStatusOptions.map(
          (s): JSX.Element => (
            <MenuItem key={s} onClick={(): void => handleToggleClaimStatus(s)}>
              <ListItemIcon>
                <Checkbox checked={applied.claimStatuses.includes(s)} size="small" />
              </ListItemIcon>
              <ListItemText primary={s} />
            </MenuItem>
          )
        )}
      </Box>
    );
  };

  const renderRoStatusContent = (): JSX.Element => (
    <Box>
      {RO_STATUS_OPTIONS.map(
        (opt): JSX.Element => (
          <MenuItem key={opt.value} onClick={(): void => handleToggleRoStatus(opt.value)}>
            <ListItemIcon>
              <Checkbox checked={applied.repairOrderStatuses.includes(opt.value)} size="small" />
            </ListItemIcon>
            <ListItemText primary={opt.label} />
          </MenuItem>
        )
      )}
    </Box>
  );

  const renderBacklogContent = (): JSX.Element => (
    <Box>
      <MenuItem onClick={(): void => handleBacklogChange(true)}>
        <ListItemIcon>
          <Checkbox checked={applied.backlog === true} size="small" />
        </ListItemIcon>
        <ListItemText primary="Yes" />
      </MenuItem>
      <MenuItem onClick={(): void => handleBacklogChange(false)}>
        <ListItemIcon>
          <Checkbox checked={applied.backlog === false} size="small" />
        </ListItemIcon>
        <ListItemText primary="No" />
      </MenuItem>
    </Box>
  );

  const renderDateRangeContent = (): JSX.Element => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.5 }}>
      <TextField
        size="small"
        type="date"
        label="From"
        InputLabelProps={{ shrink: true }}
        value={applied.createdAtFrom ?? ''}
        onChange={handleDateChange('createdAtFrom')}
      />
      <TextField
        size="small"
        type="date"
        label="To"
        InputLabelProps={{ shrink: true }}
        value={applied.createdAtTo ?? ''}
        onChange={handleDateChange('createdAtTo')}
      />
    </Box>
  );

  const renderSectionContent = (): JSX.Element | null => {
    switch (activeSection) {
      case 'dealers':
        return renderDealersContent();
      case 'oems':
        return renderOemContent();
      case 'wipStatus':
        return renderWipStatusContent();
      case 'claimStatus':
        return renderClaimStatusContent();
      case 'roStatus':
        return renderRoStatusContent();
      case 'backlog':
        return renderBacklogContent();
      case 'dateRange':
        return renderDateRangeContent();
      default:
        return null;
    }
  };

  const activeSectionLabel = SECTIONS.find((s): boolean => s.key === activeSection)?.label ?? '';

  return (
    <Popper open={isOpen} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 1300 }}>
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          elevation={8}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ width: LEFT_PANEL_WIDTH, minHeight: 200 }}>
            <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5, pb: 1, fontWeight: 600 }}>
              Filters
            </Typography>
            <Divider />
            {SECTIONS.filter((s): boolean => s.visible).map(
              (section): JSX.Element => (
                <MenuItem
                  key={section.key}
                  selected={activeSection === section.key}
                  onClick={(): void => setActiveSection(section.key)}
                  sx={{ justifyContent: 'space-between' }}
                >
                  <ListItemText primary={section.label} />
                  <ChevronRightIcon fontSize="small" color="action" />
                </MenuItem>
              )
            )}
          </Box>

          {activeSection && (
            <Box
              sx={{
                width: 'max-content',
                minWidth: 280,
                maxWidth: 480,
                maxHeight: 420,
                overflowY: 'auto',
                overflowX: 'hidden',
                borderLeft: (theme): string => `1px solid ${theme.palette.divider}`,
                minHeight: 200,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', px: 1, pt: 1, pb: 0.5 }}>
                <IconButton size="small" onClick={handleBack} sx={{ mr: 0.5 }}>
                  <KeyboardBackspaceIcon fontSize="small" />
                </IconButton>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {activeSectionLabel}
                </Typography>
              </Box>
              <Divider />
              {renderSectionContent()}
            </Box>
          )}
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default WIPFiltersDropdown;
